import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/AuthContext';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { getContrastColor, COLOR_PRIMARY, COLOR_SUCCESS, COLOR_SECONDARY } from '../../shared/colorUtils';

const UserProfileView = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: loading, isError } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: () => api.get(`accounts/api/users/${userId}/`),
  });
  const error = isError ? t('profile.view.errorLoading') : null;

  useRealtimeEvents((e) => {
    if (e.model === 'auth.user' && String(e.pk) === String(userId)) {
      queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
    }
  });

  if (loading) {
    return (
      <div className="container-fluid px-4">
        <CenteredLoader />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container-fluid px-4">
        <div className="alert alert-danger mt-3">{error || t('profile.view.notFound')}</div>
      </div>
    );
  }

  const isOwnProfile = currentUser && currentUser.id === profile.id;
  const photoUrl = profile.profile?.photo || null;
  const social = profile.profile?.social_media_links || {};

  const socialIcons = [
    { key: 'facebook',  label: 'Facebook',  icon: 'brand-facebook',  color: '#1877f2' },
    { key: 'twitter',   label: 'Twitter/X', icon: 'brand-x',         color: '#000'    },
    { key: 'instagram', label: 'Instagram', icon: 'brand-instagram',  color: '#e1306c' },
    { key: 'linkedin',  label: 'LinkedIn',  icon: 'brand-linkedin',   color: '#0a66c2' },
    { key: 'github',    label: 'GitHub',    icon: 'brand-github',     color: '#333'    },
    { key: 'website',   label: t('profile.view.website'), icon: 'world', color: COLOR_PRIMARY },
  ];

  return (
    <div className="page-wrapper">
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-fluid px-4">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">{t('profile.view.pretitle')}</div>
              <h2 className="page-title">{profile.full_name || profile.username}</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-outline-secondary" onClick={() => window.history.back()}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 12l14 0" /><path d="M5 12l6 6" /><path d="M5 12l6 -6" />
                  </svg>
                  {t('common.back')}
                </button>
                {isOwnProfile && (
                  <Link to="/accounts/profile" className="btn btn-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                      <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                      <path d="M16 5l3 3" />
                    </svg>
                    {t('profile.view.editProfile')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-fluid px-4">
          <div className="row">
            {/* Left column — avatar + social */}
            <div className="col-md-4 col-lg-3">
              <div className="card">
                <div className="card-body text-center">
                  {photoUrl ? (
                    <span
                      className="avatar avatar-xl mb-3"
                      style={{ backgroundImage: `url("${photoUrl}")` }}
                    />
                  ) : (
                    <span className="avatar avatar-xl mb-3">
                      {(profile.full_name || profile.username || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <h3 className="mb-0">{profile.full_name || profile.username}</h3>
                  <div className="text-muted mb-2">@{profile.username}</div>

                  {profile.is_staff && (
                    <span
                      className="badge mb-2"
                      style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}
                    >
                      {t('profile.view.staff')}
                    </span>
                  )}
                  {!profile.is_active && (
                    <span
                      className="badge mb-2 ms-1"
                      style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}
                    >
                      {t('profile.view.inactive')}
                    </span>
                  )}

                  {profile.profile?.bio && (
                    <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                      {profile.profile.bio}
                    </p>
                  )}
                </div>

                {/* Social links */}
                {socialIcons.some(s => social[s.key]) && (
                  <div className="card-footer">
                    <div className="d-flex justify-content-center gap-2 flex-wrap">
                      {socialIcons.map(s => social[s.key] ? (
                        <a
                          key={s.key}
                          href={social[s.key]}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={s.label}
                          className="btn btn-icon btn-sm btn-ghost-secondary"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke={s.color} fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <use href={`#tabler-${s.icon}`} />
                          </svg>
                          <span className="visually-hidden">{s.label}</span>
                        </a>
                      ) : null)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right column — details */}
            <div className="col-md-8 col-lg-9">
              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">{t('profile.view.personalInfo')}</h3>
                </div>
                <div className="card-body">
                  <div className="datagrid">
                    {profile.email && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('profile.email')}</div>
                        <div className="datagrid-content">
                          <a href={`mailto:${profile.email}`}>{profile.email}</a>
                        </div>
                      </div>
                    )}
                    <div className="datagrid-item">
                      <div className="datagrid-title">{t('profile.phone')}</div>
                      <div className="datagrid-content">
                        {profile.profile?.phone
                          ? <a href={`tel:${profile.profile.phone}`}>{profile.profile.phone}</a>
                          : '-'}
                      </div>
                    </div>
                    {profile.profile?.residence && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('profile.view.residence')}</div>
                        <div className="datagrid-content">{profile.profile.residence}</div>
                      </div>
                    )}
                    {profile.profile?.birthdate && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('profile.view.birthdate')}</div>
                        <div className="datagrid-content">
                          {new Date(profile.profile.birthdate).toLocaleDateString('cs-CZ')}
                          {profile.profile.age != null && (
                            <span className="text-muted ms-1">({profile.profile.age} {t('profile.view.years')})</span>
                          )}
                        </div>
                      </div>
                    )}
                    {profile.date_joined_formatted && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('profile.view.memberSince')}</div>
                        <div className="datagrid-content">
                          {new Date(profile.date_joined).toLocaleDateString('cs-CZ')}
                        </div>
                      </div>
                    )}
                    {profile.last_login_formatted && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('profile.view.lastLogin')}</div>
                        <div className="datagrid-content">
                          {new Date(profile.last_login).toLocaleDateString('cs-CZ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Social links as full rows (if any) */}
              {socialIcons.some(s => social[s.key] || (s.key === 'website' && profile.profile?.website)) && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{t('profile.view.onlinePresence')}</h3>
                  </div>
                  <div className="card-body">
                    <div className="datagrid">
                      {socialIcons.map(s => {
                        const url = s.key === 'website' ? profile.profile?.website : social[s.key];
                        return url ? (
                          <div key={s.key} className="datagrid-item">
                            <div className="datagrid-title">{s.label}</div>
                            <div className="datagrid-content">
                              <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileView;
